<script setup lang="ts">
/**
 * What a table is called, how it fits the page and how it is drawn: the
 * settings of the table as a whole, as opposed to its cells.
 *
 * Its own component because it appears twice, in the inspector and in the
 * table editor, and two copies of three fields drift the day one of them gains
 * a fourth. The inspector shows its two halves in tabs of their own: the
 * table's settings (`settings`: its name, caption, fit and column widths) and
 * how it is drawn (`look`); the editor, which has the room, shows both.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { TableFit, TableSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'
import TableLookFields from '@/sections/table/TableLookFields.vue'
import { columnCount } from '@/sections/table/spans'
import { defaultImportedWidths, equalColumnWidths, narrowColumns } from '@/sections/table/widths'

const { t } = useI18n()

const props = defineProps<{
  section: TableSection
  /** Which half: the table's settings, or how it is drawn. Both when not said. */
  part?: 'settings' | 'look' | undefined
}>()

function shows(part: 'settings' | 'look'): boolean {
  return props.part === undefined || props.part === part
}
const emit = defineEmits<{ change: [section: TableSection] }>()

const FIT_OPTIONS = ['normal', 'shrink', 'compact'] as const satisfies readonly TableFit[]

const columns = computed(() => Math.max(columnCount(props.section), 1))

/** Shares are only in force when there is exactly one per column; see `usableColumnWidths`. */
const widthsEnabled = computed(() => props.section.columnWidths.length === columns.value)

const widthDraft = computed(() =>
  widthsEnabled.value ? props.section.columnWidths : equalColumnWidths(columns.value),
)

/** The columns too narrow for their own words, which break letter by letter. */
const narrow = computed(() =>
  widthsEnabled.value
    ? narrowColumns(widthDraft.value, [
        props.section.header.map((cell) => cell.value),
        ...props.section.rows.map((row) => row.map((cell) => cell.value)),
      ])
    : new Set<number>(),
)

function set(changes: Partial<TableSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setColumnWidth(index: number, raw: string): void {
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) return
  const next = [...widthDraft.value]
  next[index] = Math.round(parsed * 10) / 10
  set({ columnWidths: next })
}
</script>

<template>
  <div class="fields">
    <template v-if="shows('settings')">
      <InspectorField :label="t('inspector.table.title')">
        <input
          class="b-input"
          :value="section.title"
          @input="set({ title: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField
        :label="t('inspector.table.caption')"
        :hint="t('inspector.table.captionHint')"
      >
        <input
          class="b-input"
          :value="section.caption"
          @input="set({ caption: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.table.fit')" :hint="t('inspector.table.fitHint')">
        <div class="segments" role="radiogroup" :aria-label="t('inspector.table.fit')">
          <button
            v-for="option in FIT_OPTIONS"
            :key="option"
            type="button"
            role="radio"
            class="segment"
            :class="{ 'segment--on': option === section.fit }"
            :aria-checked="option === section.fit"
            @click="set({ fit: option })"
          >
            {{ t(`inspector.table.fitOptions.${option}`) }}
          </button>
        </div>
      </InspectorField>

      <InspectorField
        :label="t('inspector.table.columnWidths')"
        :hint="t('inspector.table.columnWidthsHint')"
      >
        <div class="widths">
          <div class="widths__ops">
            <button
              v-if="!widthsEnabled"
              type="button"
              class="b-btn b-btn--outline"
              @click="set({ columnWidths: defaultImportedWidths(columns) })"
            >
              {{ t('inspector.table.columnWidthsSet') }}
            </button>
            <template v-else>
              <button
                type="button"
                class="b-btn b-btn--outline"
                @click="set({ columnWidths: equalColumnWidths(columns) })"
              >
                {{ t('inspector.table.columnWidthsEqual') }}
              </button>
              <button type="button" class="b-btn b-btn--outline" @click="set({ columnWidths: [] })">
                {{ t('inspector.table.columnWidthsAuto') }}
              </button>
            </template>
          </div>
          <template v-if="widthsEnabled">
            <!-- The shares as they divide the table, column by column. -->
            <div class="share" aria-hidden="true">
              <span
                v-for="(width, column) in widthDraft"
                :key="column"
                class="share__part"
                :class="{ 'share__part--narrow': narrow.has(column) }"
                :style="{ flexGrow: width }"
                >{{ column + 1 }}</span
              >
            </div>
            <div class="widths__list">
              <label
                v-for="(width, column) in widthDraft"
                :key="column"
                class="widths__row"
                :class="{ 'widths__row--narrow': narrow.has(column) }"
              >
                <span class="widths__number">{{ column + 1 }}</span>
                <span class="widths__name">
                  {{
                    section.header[column]?.value?.trim() ||
                    t('inspector.table.columnPlaceholder', { number: column + 1 })
                  }}
                </span>
                <input
                  class="b-input widths__input"
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  :value="width"
                  @change="setColumnWidth(column, ($event.target as HTMLInputElement).value)"
                />
                <span class="widths__unit">%</span>
              </label>
            </div>
            <p v-if="narrow.size > 0" class="widths__warning" role="status">
              {{ t('inspector.table.columnsNarrow', { count: narrow.size }, narrow.size) }}
            </p>
          </template>
        </div>
      </InspectorField>
    </template>

    <TableLookFields v-if="shows('look')" :section="section" @change="emit('change', $event)" />
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.segments {
  display: flex;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 30px;
  padding: 0 var(--sp-3);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
  box-shadow: none;
}

.widths {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.widths__ops {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
}

.widths__ops .b-btn {
  height: 26px;
  padding: 0 var(--sp-4);
  font-size: var(--text-sm);
}

.widths__list {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

/* The table's width, divided as the shares divide it: a column too narrow
   for its words in the warning colour. */
.share {
  display: flex;
  gap: 2px;
  height: 22px;
}

.share__part {
  flex: 1 1 0;
  min-width: 14px;
  overflow: hidden;
  border-radius: var(--radius-xs);
  background: var(--accent-quiet);
  box-shadow: inset 0 0 0 1px var(--accent-line);
  color: var(--accent);
  font-size: 10px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.share__part--narrow {
  background: var(--color-warning-soft);
  box-shadow: inset 0 0 0 1px var(--color-warning);
  color: var(--warn);
}

.widths__row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 64px auto;
  gap: var(--sp-3);
  align-items: center;
}

.widths__number {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-xs);
  background: var(--accent-quiet);
  color: var(--accent);
  font-size: 10px;
  font-weight: 600;
}

.widths__row--narrow .widths__number {
  background: var(--color-warning-soft);
  color: var(--warn);
}

.widths__warning {
  margin: 0;
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--radius);
  background: var(--color-warning-soft);
  color: var(--warn);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.widths__name {
  overflow: hidden;
  color: var(--ink-soft);
  font-size: var(--text-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widths__input {
  height: 28px;
  padding: 0 var(--sp-3);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.widths__unit {
  color: var(--ink-faint);
  font-size: var(--text-sm);
}
</style>
